






import Vue from "vue";
import Component from "vue-class-component";
import {Prop, Watch} from "vue-property-decorator";
import Chart from "chart.js";

const CHART_COLORS = [
    {bg: "rgba(64, 158, 255, 0.6)", border: "rgba(64, 158, 255, 1)", solid: "rgba(64, 158, 255, 0.7)"},
    {bg: "rgba(103, 194, 58, 0.6)", border: "rgba(103, 194, 58, 1)", solid: "rgba(103, 194, 58, 0.7)"},
    {bg: "rgba(230, 162, 60, 0.6)", border: "rgba(230, 162, 60, 1)", solid: "rgba(230, 162, 60, 0.7)"},
    {bg: "rgba(245, 108, 108, 0.6)", border: "rgba(245, 108, 108, 1)", solid: "rgba(245, 108, 108, 0.7)"},
    {bg: "rgba(144, 147, 153, 0.6)", border: "rgba(144, 147, 153, 1)", solid: "rgba(144, 147, 153, 0.7)"},
    {bg: "rgba(155, 89, 182, 0.6)", border: "rgba(155, 89, 182, 1)", solid: "rgba(155, 89, 182, 0.7)"},
    {bg: "rgba(52, 152, 219, 0.6)", border: "rgba(52, 152, 219, 1)", solid: "rgba(52, 152, 219, 0.7)"},
    {bg: "rgba(26, 188, 156, 0.6)", border: "rgba(26, 188, 156, 1)", solid: "rgba(26, 188, 156, 0.7)"},
    {bg: "rgba(241, 196, 15, 0.6)", border: "rgba(241, 196, 15, 1)", solid: "rgba(241, 196, 15, 0.7)"},
    {bg: "rgba(231, 76, 60, 0.6)", border: "rgba(231, 76, 60, 1)", solid: "rgba(231, 76, 60, 0.7)"}
];

const frFormat = (value: any) =>
    new Intl.NumberFormat("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value);

const commonScales = {
    yAxes: [{
        ticks: {
            beginAtZero: true,
            callback: frFormat
        }
    }],
    xAxes: [{
        ticks: {
            maxRotation: 45,
            minRotation: 45
        }
    }]
};

interface ChartBuildResult {
    type: string;
    datasets: any[];
    options: any;
}

const BUILDERS: Record<string, (data: any[], config: any) => ChartBuildResult> = {
    bar: (data, config) => ({
        type: "bar",
        datasets: [{
            label: config.title,
            data: data.map((item) => item[config.y_axis]),
            backgroundColor: CHART_COLORS[0].bg,
            borderColor: CHART_COLORS[0].border,
            borderWidth: 1
        }],
        options: {
            legend: {display: false},
            scales: commonScales
        }
    }),

    grouped_bar: (data, config) => {
        const yAxes = Array.isArray(config.y_axis) ? config.y_axis : [config.y_axis];
        return {
            type: "bar",
            datasets: yAxes.map((key: string, i: number) => ({
                label: key,
                data: data.map((item) => item[key]),
                backgroundColor: CHART_COLORS[i % CHART_COLORS.length].bg,
                borderColor: CHART_COLORS[i % CHART_COLORS.length].border,
                borderWidth: 1
            })),
            options: {
                legend: {display: yAxes.length > 1},
                scales: commonScales
            }
        };
    },

    line: (data, config) => ({
        type: "line",
        datasets: [{
            label: config.title,
            data: data.map((item) => item[config.y_axis]),
            backgroundColor: CHART_COLORS[0].solid,
            borderColor: CHART_COLORS[0].border,
            borderWidth: 2,
            pointBackgroundColor: CHART_COLORS[0].border,
            pointRadius: 4,
            fill: true
        }],
        options: {
            legend: {display: false},
            scales: commonScales
        }
    }),

    pie: (data, config) => {
        const values = data.map((item) => item[config.y_axis]);
        return {
            type: "pie",
            datasets: [{
                data: values,
                backgroundColor: values.map((_: any, i: number) => CHART_COLORS[i % CHART_COLORS.length].solid),
                borderColor: values.map((_: any, i: number) => CHART_COLORS[i % CHART_COLORS.length].border),
                borderWidth: 1
            }],
            options: {
                legend: {display: true, position: "right"},
                tooltips: {
                    callbacks: {
                        label: (tooltipItem: any, chartData: any) => {
                            const dataset = chartData.datasets[tooltipItem.datasetIndex];
                            const value = dataset.data[tooltipItem.index];
                            const label = chartData.labels[tooltipItem.index];
                            return `${label}: ${frFormat(value)}`;
                        }
                    }
                }
            }
        };
    }
};

@Component
export default class ChartRenderer extends Vue {
    @Prop({required: true}) public chartData!: any[];
    @Prop({required: true}) public chartConfig!: any;

    private chart: any = null;

    public mounted(): void {
        this.renderChart();
    }

    public beforeDestroy(): void {
        if (this.chart) {
            this.chart.destroy();
        }
    }

    @Watch("chartData")
    @Watch("chartConfig")
    private onDataChange(): void {
        this.renderChart();
    }

    private renderChart(): void {
        if (this.chart) {
            this.chart.destroy();
        }

        const canvas = this.$refs.chartCanvas as HTMLCanvasElement;
        if (!canvas) {
            return;
        }

        const builder = BUILDERS[this.chartConfig.chart_type];
        if (!builder) {
            return;
        }

        const labels = this.chartData.map((item: any) => item[this.chartConfig.x_axis]);
        const {type, datasets, options} = builder(this.chartData, this.chartConfig);

        this.chart = new Chart(canvas, {
            type,
            data: {labels, datasets},
            options: {
                responsive: true,
                maintainAspectRatio: true,
                title: {
                    display: true,
                    text: this.chartConfig.title,
                    fontSize: 16,
                    fontStyle: "bold"
                },
                ...options
            }
        });
    }
}
